import { jsx as _jsx } from "react/jsx-runtime";
import { useMap } from '@vis.gl/react-google-maps';
import { useMemo } from 'react';
import { googleCoordinates } from '../utils/googleCoordinates';
import OffScreenIndicator from './OffScreenIndicator';
const MAX_OFF_SCREEN_DISTANCE_M = 500;
const ZERO_INSETS = { top: 0, right: 0, bottom: 0, left: 0 };
// At zoom 0 the world is 256 px wide, spanning ~40075 km at the equator.
const EQUATOR_METERS_PER_PIXEL_AT_ZOOM_0 = 156543.03392;
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const buildIndicators = (bounds, zoom, projection, markers) => {
    const scale = Math.pow(2, zoom);
    const projectToPixel = (latitude, longitude) => {
        const point = projection.fromLatLngToPoint(new google.maps.LatLng(latitude, longitude));
        if (!point)
            return undefined;
        return { x: point.x * scale, y: point.y * scale };
    };
    const northEastPixel = projectToPixel(bounds.north, bounds.east);
    const southWestPixel = projectToPixel(bounds.south, bounds.west);
    if (!northEastPixel || !southWestPixel)
        return [];
    const xMin = southWestPixel.x;
    const xMax = northEastPixel.x;
    const yMin = northEastPixel.y; // Mercator y grows southward, so north → smaller y.
    const yMax = southWestPixel.y;
    if (xMax <= xMin || yMax <= yMin)
        return [];
    const centerLatitude = (bounds.north + bounds.south) / 2;
    const metersPerPixel = (EQUATOR_METERS_PER_PIXEL_AT_ZOOM_0 * Math.cos((centerLatitude * Math.PI) / 180)) / scale;
    const centerX = (xMin + xMax) / 2;
    const centerY = (yMin + yMax) / 2;
    const indicators = [];
    for (const marker of markers) {
        const projected = projectToPixel(marker.position.latitude, marker.position.longitude);
        if (!projected)
            continue;
        const isOffScreen = projected.x < xMin || projected.x > xMax || projected.y < yMin || projected.y > yMax;
        if (!isOffScreen)
            continue;
        const closestX = clamp(projected.x, xMin, xMax);
        const closestY = clamp(projected.y, yMin, yMax);
        const distanceMeters = Math.hypot(projected.x - closestX, projected.y - closestY) * metersPerPixel;
        if (distanceMeters > MAX_OFF_SCREEN_DISTANCE_M)
            continue;
        indicators.push({
            marker,
            leftPercentage: ((closestX - xMin) / (xMax - xMin)) * 100,
            topPercentage: ((closestY - yMin) / (yMax - yMin)) * 100,
            angleDegree: (Math.atan2(projected.x - centerX, -(projected.y - centerY)) * 180) / Math.PI,
            distanceMeters,
        });
    }
    return indicators;
};
export default function OffScreenIndicatorOverlay({ testId, mapNodeId, bounds, zoom, allMarkers, safeAreaInsets = ZERO_INSETS, }) {
    const map = useMap(mapNodeId);
    const projection = map?.getProjection();
    const indicators = useMemo(() => {
        if (!bounds || zoom === undefined || !projection)
            return [];
        return buildIndicators(bounds, zoom, projection, allMarkers);
    }, [bounds, zoom, projection, allMarkers]);
    if (indicators.length === 0)
        return null;
    const handleIndicatorClick = (marker) => {
        if (!map)
            return;
        map.panTo(googleCoordinates(marker.position));
    };
    const arrowsTestId = testId.append('arrows');
    return (_jsx("div", { className: "z-floating pointer-events-none absolute", style: {
            top: safeAreaInsets.top,
            right: safeAreaInsets.right,
            bottom: safeAreaInsets.bottom,
            left: safeAreaInsets.left,
        }, "data-testid": arrowsTestId, children: indicators.map((indicator) => (_jsx(OffScreenIndicator, { testId: arrowsTestId.append(indicator.marker.parkingSlug), marker: indicator.marker, leftPercentage: indicator.leftPercentage, topPercentage: indicator.topPercentage, angleDegree: indicator.angleDegree, distanceMeters: indicator.distanceMeters, onClick: handleIndicatorClick }, indicator.marker.parkingSlug))) }));
}
